import { useCallback } from "react"
import { useTranslation } from "react-i18next"
import { gql } from "@apollo/client"
import { useMutation, InjectedOptions } from "@oysterhr/core"
import { Modal, Button, useToast, Toast, Text } from "@oysterhr/ui"
import { Engagement } from "@oysterhr/schema"

export const APPROVE_CANCELLATION_MUTATION = gql`
  mutation ApproveTerminationCancellation($terminationId: ID!) {
    approveTerminationCancellation(input: { terminationId: $terminationId }) {
      errors
    }
  }
`

const useApproveCancellationMutation = (options?: InjectedOptions) =>
  useMutation(APPROVE_CANCELLATION_MUTATION, options)

interface ApproveTerminationCancellationModalProps {
  engagement: Engagement
  setShowModal: (showModal: boolean) => void
  refetch: () => void
}

const ApproveTerminationCancellationModal = ({
  engagement,
  setShowModal,
  refetch,
}: ApproveTerminationCancellationModalProps) => {
  const { t } = useTranslation("colleague_interface/EmployeeProfilePage")
  const toast = useToast()

  const [approveCancellationMutation] = useApproveCancellationMutation()

  const activeTermination = engagement.termination
  const isGlobalPayroll = engagement?.isPayrollTeamMember

  const callApproveCancellationMutation = useCallback(async () => {
    await approveCancellationMutation({
      variables: { terminationId: activeTermination?.id },
      handlePayloadErrors: true,
    } as InjectedOptions)

    setShowModal(false)

    toast({
      render: (
        <Toast variation="success">
          <Toast.Content>{t("termination.cancellationApproval.success")}</Toast.Content>
        </Toast>
      ),
    })

    refetch()
  }, [activeTermination?.id, approveCancellationMutation, setShowModal, toast, refetch, t])

  if (isGlobalPayroll) {
    return (
      <Modal onRequestClose={() => setShowModal(false)} isOpen>
        <Modal.Header>{t("offboarding.gpApproveCancelTerminationModal.title")}</Modal.Header>
        <Modal.Content>
          <Text>
            {t("offboarding.gpApproveCancelTerminationModal.message", {
              tmName: engagement?.teamMemberName,
            })}
          </Text>
          <Text className="mt-2">{t("offboarding.gpApproveCancelTerminationModal.proceed")}</Text>
        </Modal.Content>
        <Modal.Footer>
          <Button variation="secondary" onClick={() => setShowModal(false)}>
            {t("offboarding.gpApproveCancelTerminationModal.cancel")}
          </Button>

          <Button type="button" onClick={callApproveCancellationMutation}>
            {t("offboarding.gpApproveCancelTerminationModal.submit")}
          </Button>
        </Modal.Footer>
      </Modal>
    )
  } else {
    return (
      <Modal onRequestClose={() => setShowModal(false)} isOpen>
        <Modal.Header>{t("termination.cancellationApproval.title")}</Modal.Header>
        <Modal.Footer data-testid="approve-termination-cancellation-modal">
          <Button variation="secondary" onClick={() => setShowModal(false)} className="w-44">
            {t("termination.cancellationApproval.close")}
          </Button>
          <Button variation="secondary" className="w-44" onClick={callApproveCancellationMutation}>
            {t("termination.cancellationApproval.submit")}
          </Button>
        </Modal.Footer>
      </Modal>
    )
  }
}

export default ApproveTerminationCancellationModal
